import { useEffect, useRef, useState } from 'react';
import type { CompanionData, CompanionGroup, CompanionItem, CompanionCriterion } from '@garuda/shared';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Box,
  Grid,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

interface CompanionEditorProps {
  value: Partial<CompanionData> | null;
  onChange: (data: Partial<CompanionData> | null) => void;
  gameType: string;
  groups?: { label: string; difficulty: number }[];
  items?: { title: string }[];
  criteria?: { axis: string; label: string }[];
}

/** Serialize prop labels to a stable key for change detection */
function labelsKey(arr: { label?: string; title?: string }[] | undefined): string {
  if (!arr || arr.length === 0) return '';
  return arr.map((a) => a.label ?? a.title ?? '').join('|');
}

export const CompanionEditor = ({
  value,
  onChange,
  gameType,
  groups,
  items,
  criteria,
}: CompanionEditorProps) => {
  const [expanded, setExpanded] = useState(false);

  const data = value || {};

  const update = (patch: Partial<CompanionData>) => {
    onChange({ ...data, ...patch });
  };

  // Track previous prop labels so we can detect when they change
  const prevGroupsKey = useRef(labelsKey(groups));
  const prevItemsKey = useRef(labelsKey(items));
  const prevCriteriaKey = useRef(labelsKey(criteria));

  useEffect(() => {
    const gk = labelsKey(groups);
    if (gk !== prevGroupsKey.current) {
      prevGroupsKey.current = gk;
      // Props changed — re-derive groups from new props, preserving explanations where labels match
      if (groups && groups.length > 0) {
        const oldByLabel = new Map((data.groups || []).map((g) => [g.label, g.explanation]));
        onChange({ ...data, groups: groups.map((g) => ({ label: g.label, explanation: oldByLabel.get(g.label) || '' })) });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelsKey(groups)]);

  useEffect(() => {
    const ik = labelsKey(items);
    if (ik !== prevItemsKey.current) {
      prevItemsKey.current = ik;
      if (items && items.length > 0) {
        const oldByTitle = new Map((data.items || []).map((i) => [i.title, i.context]));
        onChange({ ...data, items: items.map((i) => ({ title: i.title, context: oldByTitle.get(i.title) || '' })) });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelsKey(items)]);

  useEffect(() => {
    const ck = labelsKey(criteria);
    if (ck !== prevCriteriaKey.current) {
      prevCriteriaKey.current = ck;
      if (criteria && criteria.length > 0) {
        const oldByKey = new Map((data.criteria || []).map((c) => [`${c.axis}:${c.label}`, c.explanation]));
        onChange({
          ...data,
          criteria: criteria.map((c) => ({
            axis: c.axis as 'x' | 'y',
            label: c.label,
            explanation: oldByKey.get(`${c.axis}:${c.label}`) || '',
          })),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelsKey(criteria)]);

  // Derive display values — prefer state, fall back to props
  const companionGroups: CompanionGroup[] =
    data.groups ??
    (groups ? groups.map((g) => ({ label: g.label, explanation: '' })) : []);

  const companionItems: CompanionItem[] =
    data.items ??
    (items ? items.map((i) => ({ title: i.title, context: '' })) : []);

  const companionCriteria: CompanionCriterion[] =
    data.criteria ??
    (criteria
      ? criteria.map((c) => ({ axis: c.axis as 'x' | 'y', label: c.label, explanation: '' }))
      : []);

  const triviaList = data.trivia || [];

  return (
    <Accordion expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">
          Companion Article {data.headline ? `— "${data.headline}"` : '(Optional)'}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Headline */}
          <Grid item xs={12} sm={8}>
            <TextField
              label="Headline"
              value={data.headline || ''}
              onChange={(e) => update({ headline: e.target.value })}
              fullWidth
              size="small"
              helperText="Required to include companion data"
            />
          </Grid>

          {/* Difficulty */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={data.difficulty ?? ''}
                onChange={(e) =>
                  update({ difficulty: e.target.value ? Number(e.target.value) : undefined })
                }
                label="Difficulty"
              >
                <MenuItem value="">None</MenuItem>
                {[1, 2, 3, 4, 5].map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Summary */}
          <Grid item xs={12}>
            <TextField
              label="Summary"
              value={data.summary || ''}
              onChange={(e) => update({ summary: e.target.value })}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
          </Grid>

          {/* Thematic Note */}
          <Grid item xs={12}>
            <TextField
              label="Thematic Note"
              value={data.thematicNote || ''}
              onChange={(e) => update({ thematicNote: e.target.value })}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
          </Grid>

          {/* Trivia */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Trivia</Typography>
            {triviaList.map((t, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  value={t}
                  onChange={(e) => {
                    const updated = [...triviaList];
                    updated[i] = e.target.value;
                    update({ trivia: updated });
                  }}
                  fullWidth
                  size="small"
                  placeholder={`Trivia #${i + 1}`}
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => update({ trivia: triviaList.filter((_, j) => j !== i) })}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => update({ trivia: [...triviaList, ''] })}
            >
              Add Trivia
            </Button>
          </Grid>

          {/* Groups (connections only) */}
          {gameType === 'connections' && companionGroups.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Group Explanations</Typography>
              {companionGroups.map((g, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ minWidth: 120, fontWeight: 'bold' }}>
                    {g.label}
                  </Typography>
                  <TextField
                    value={g.explanation || ''}
                    onChange={(e) => {
                      const updated = [...companionGroups];
                      updated[i] = { ...updated[i], explanation: e.target.value };
                      update({ groups: updated });
                    }}
                    fullWidth
                    size="small"
                    placeholder="Explanation for this group"
                  />
                </Box>
              ))}
            </Grid>
          )}

          {/* Items (timeline only) */}
          {gameType === 'timeline' && companionItems.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Item Context</Typography>
              {companionItems.map((item, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ minWidth: 160, fontWeight: 'bold' }}>
                    {item.title}
                  </Typography>
                  <TextField
                    value={item.context || ''}
                    onChange={(e) => {
                      const updated = [...companionItems];
                      updated[i] = { ...updated[i], context: e.target.value };
                      update({ items: updated });
                    }}
                    fullWidth
                    size="small"
                    placeholder="Context for this item"
                  />
                </Box>
              ))}
            </Grid>
          )}

          {/* Criteria (grid only) */}
          {gameType === 'grid' && companionCriteria.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Criteria Explanations</Typography>
              {companionCriteria.map((c, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ minWidth: 160, fontWeight: 'bold' }}>
                    [{c.axis.toUpperCase()}] {c.label}
                  </Typography>
                  <TextField
                    value={c.explanation || ''}
                    onChange={(e) => {
                      const updated = [...companionCriteria];
                      updated[i] = { ...updated[i], explanation: e.target.value };
                      update({ criteria: updated });
                    }}
                    fullWidth
                    size="small"
                    placeholder="Explanation for this criterion"
                  />
                </Box>
              ))}
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
